import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import { CategoryService } from './categories'
import { AttachmentService, type VideoAttachment } from './attachments'

export type Video = Database['public']['Tables']['videos']['Row']
export type VideoInsert = Database['public']['Tables']['videos']['Insert']
export type VideoUpdate = Database['public']['Tables']['videos']['Update']

export type VideoWithCategory = Video & {
  categories?: {
    id: string
    name: string
    slug: string
  } | null
  tags?: Array<{ name: string; slug: string }>
  attachments?: VideoAttachment[]
}

export type UserVideoProgress = Database['public']['Tables']['user_video_progress']['Row']

export type WatchHistoryItem = {
  id: string
  user_id: string
  video_id: string
  watched_at: string
  watch_duration: number
  created_at: string
  updated_at: string
  video?: VideoWithCategory
}

export class VideoService {
  // Get all videos with optional filtering
  static async getVideos(filters?: {
    category?: string
    category_id?: string
    tags?: string[]
    search?: string
    limit?: number
    offset?: number
    excludeId?: string
  }): Promise<VideoWithCategory[]> {
    let query = supabase
      .from('videos')
      .select(`
        *,
        categories!category_id (
          id,
          name,
          slug
        ),
        video_tags (
          tags (
            id,
            name,
            slug
          )
        )
      `)
      .order('created_at', { ascending: false })
      .is('school_id', null)

    // Support both category_id (preferred) and category (legacy) filtering
    if (filters?.category_id) {
      query = query.eq('category_id', filters.category_id)
    } else if (filters?.category && filters.category !== 'all') {
      // Legacy support: filter by category string
      query = query.eq('category', filters.category)
    }

    if (filters?.tags && filters.tags.length > 0) {
      query = query.overlaps('tags', filters.tags)
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }

    if (filters?.excludeId) {
      query = query.neq('id', filters.excludeId)
    }

    if (filters?.limit) {
      query = query.limit(filters.limit)
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
    }

    const { data, error } = await query

    if (error) throw error
    
    // Transform data to include tags array
    return (data || []).map(video => ({
      ...video,
      categories: video.categories || null,
      tags: video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
    }))
  }

  // Get a single video by ID
  static async getVideo(id: string): Promise<VideoWithCategory | null> {
    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        categories!category_id (
          id,
          name,
          slug
        ),
        video_tags (
          tags (
            id,
            name,
            slug
          )
        )
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    
    if (!data) return null
    
    // Transform data to include tags array
    const transformedVideo: VideoWithCategory = {
      ...data,
      categories: data.categories || null,
      tags: data.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
    }

    // Fetch attachments for this video
    try {
      const attachments = await AttachmentService.getVideoAttachments(id)
      transformedVideo.attachments = attachments
    } catch (error) {
      console.warn('Failed to fetch video attachments:', error)
      transformedVideo.attachments = []
    }

    return transformedVideo
  }

  // Get video categories (uses proper categories table)
  static async getCategories(): Promise<Array<{ id: string; name: string; count: number }>> {
    // Use CategoryService for proper category management
    const categories = await CategoryService.getCategories(true)

    const totalCount = categories.reduce((sum, cat) => sum + cat.video_count, 0)

    return [
      { id: 'all', name: 'すべて', count: totalCount },
      ...categories.map(cat => ({
        id: cat.slug, // Use slug for backward compatibility
        name: cat.name,
        count: cat.video_count
      }))
    ]
  }

  // Get user's video progress
  static async getUserProgress(userId: string, videoId?: string): Promise<UserVideoProgress[]> {
    let query = supabase
      .from('user_video_progress')
      .select('*')
      .eq('user_id', userId)

    if (videoId) {
      query = query.eq('video_id', videoId)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  // Update user's video progress
  static async updateProgress(
    userId: string,
    videoId: string,
    progressSeconds: number,
    completed: boolean = false
  ): Promise<UserVideoProgress> {
    const { data, error } = await supabase
      .from('user_video_progress')
      .upsert({
        user_id: userId,
        video_id: videoId,
        progress_seconds: progressSeconds,
        completed,
        last_watched: new Date().toISOString()
      }, {
        onConflict: 'user_id,video_id'
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Track video analytics event
  static async trackEvent(
    videoId: string,
    eventType: 'view' | 'play' | 'pause' | 'complete' | 'share',
    userId?: string,
    metadata?: Record<string, unknown>
  ) {
    const { error } = await supabase
      .from('video_analytics')
      .insert({
        video_id: videoId,
        user_id: userId || null,
        event_type: eventType,
        session_id: crypto.randomUUID(),
        metadata: metadata || null
      })

    if (error) throw error
  }

  // Increment video view count
  static async incrementViewCount(videoId: string, userId?: string): Promise<void> {
    // Track analytics event
    await this.trackEvent(videoId, 'view', userId)

    // Increment view_count in videos table (and the per-school daily bucket) using RPC function
    const { error } = await supabase.rpc('increment_video_view_count', {
      p_video_id: videoId,
      p_user_id: userId ?? null
    })

    if (error) throw error
  }

  // Admin: Create new video
  static async createVideo(video: VideoInsert, thumbnailFile?: File): Promise<Video> {
    let videoData = { ...video }

    // Upload thumbnail if provided
    if (thumbnailFile) {
      const thumbnailUrl = await this.uploadThumbnail(thumbnailFile)
      videoData = { ...videoData, thumbnail_url: thumbnailUrl }
    }

    const { data, error } = await supabase
      .from('videos')
      .insert(videoData)
      .select()
      .single()

    if (error) {
      // Clean up uploaded thumbnail if video creation failed
      if (thumbnailFile && videoData.thumbnail_url) {
        await this.deleteThumbnail(videoData.thumbnail_url)
      }
      throw error
    }

    return data
  }

  // Admin: Update video
  static async updateVideo(id: string, updates: VideoUpdate, thumbnailFile?: File): Promise<Video> {
    let updateData = { ...updates }

    // Handle thumbnail update if new file is provided
    if (thumbnailFile) {
      const currentVideo = await this.getVideo(id)

      // Upload new thumbnail
      const newThumbnailUrl = await this.uploadThumbnail(thumbnailFile, id)
      updateData = { ...updateData, thumbnail_url: newThumbnailUrl }

      // Delete old thumbnail if it exists and is from our storage
      if (currentVideo?.thumbnail_url?.includes('video-thumbnails')) {
        await this.deleteThumbnail(currentVideo.thumbnail_url)
      }
    }

    const { data, error } = await supabase
      .from('videos')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      // Clean up new thumbnail if update failed
      if (thumbnailFile && updateData.thumbnail_url) {
        await this.deleteThumbnail(updateData.thumbnail_url)
      }
      throw error
    }

    return data
  }


  // Admin: Get video analytics
  static async getAnalytics(filters?: {
    videoId?: string
    startDate?: string
    endDate?: string
  }) {
    let query = supabase
      .from('video_analytics')
      .select(`
        *,
        videos!inner(title),
        users(name)
      `)
      .order('timestamp', { ascending: false })

    if (filters?.videoId) {
      query = query.eq('video_id', filters.videoId)
    }

    if (filters?.startDate) {
      query = query.gte('timestamp', filters.startDate)
    }

    if (filters?.endDate) {
      query = query.lte('timestamp', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  // Admin: Get video statistics
  static async getVideoStats() {
    const { data: videos, error: videosError } = await supabase
      .from('videos')
      .select('id')

    if (videosError) throw videosError

    const { data: progress, error: progressError } = await supabase
      .from('user_video_progress')
      .select('completed, user_id')

    if (progressError) throw progressError

    const { data: analytics, error: analyticsError } = await supabase
      .from('video_analytics')
      .select('event_type, timestamp')
      .gte('timestamp', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()) // Last 30 days

    if (analyticsError) throw analyticsError

    const totalVideos = videos?.length || 0
    const completedVideos = progress?.filter(p => p.completed).length || 0
    const uniqueViewers = new Set(progress?.map(p => p.user_id)).size
    const totalViews = analytics?.filter(a => a.event_type === 'view').length || 0

    return {
      totalVideos,
      completedVideos,
      uniqueViewers,
      totalViews,
      averageCompletion: totalVideos > 0 ? (completedVideos / totalVideos) * 100 : 0
    }
  }

  // Save or update watch history
  static async saveWatchHistory(
    userId: string,
    videoId: string,
    watchDuration: number = 0
  ): Promise<WatchHistoryItem> {
    const { data, error } = await supabase
      .rpc('upsert_watch_history', {
        p_user_id: userId,
        p_video_id: videoId,
        p_watch_duration: watchDuration
      })

    if (error) throw error
    return data
  }

  // Get user's watch history
  static async getWatchHistory(
    userId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<WatchHistoryItem[]> {
    // Fetch more items than needed to account for filtered out videos
    // This ensures we get enough valid results even after filtering
    const fetchLimit = limit * 3

    const { data, error } = await supabase
      .from('watch_history')
      .select(`
        *,
        video:videos!video_id (
          *,
          categories!category_id (
            id,
            name,
            slug
          ),
          video_tags (
            tags (
              id,
              name,
              slug
            )
          )
        )
      `)
      .eq('user_id', userId)
      .order('watched_at', { ascending: false })
      .limit(fetchLimit)

    if (error) throw error

    // Filter out demo and chat_only videos in application code
    const filtered = (data || [])
      .filter(item =>
        item.video &&
        item.video.video_type !== 'demo' &&
        item.video.video_type !== 'chat_only'
      )
      .slice(offset, offset + limit)

    // Transform the data to match our type
    return filtered.map(item => ({
      ...item,
      video: item.video ? {
        ...item.video,
        categories: item.video.categories || null,
        // Transform video_tags to tags array with name and slug
        tags: item.video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
      } : undefined
    }))
  }

  // Get the total count of watch history items for a user
  static async getWatchHistoryCount(userId: string): Promise<number> {
    // Fetch all watch history items with their video types
    const { data, error } = await supabase
      .from('watch_history')
      .select(`
        id,
        videos!inner(video_type)
      `)
      .eq('user_id', userId)

    if (error) throw error

    // Filter in application code to ensure accurate count
    const filtered = (data || []).filter((item: any) => {
      const videoType = item.videos?.video_type
      return videoType !== 'demo' && videoType !== 'chat_only'
    })

    return filtered.length
  }

  // Get the most recently watched video for a user
  static async getMostRecentlyWatched(userId: string): Promise<WatchHistoryItem | null> {
    const history = await this.getWatchHistory(userId, 1)
    return history.length > 0 ? history[0] : null
  }

  // Upload thumbnail image to Supabase Storage
  static async uploadThumbnail(file: File, videoId?: string): Promise<string> {
    const fileExt = file.name.split('.').pop()
    const fileName = videoId 
      ? `${videoId}.${fileExt}` 
      : `thumbnail-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
    
    const { error } = await supabase.storage
      .from('video-thumbnails')
      .upload(fileName, file, {
        contentType: file.type,
        upsert: true
      })

    if (error) throw error

    // Get the public URL for the uploaded file
    const { data: { publicUrl } } = supabase.storage
      .from('video-thumbnails')
      .getPublicUrl(fileName)

    return publicUrl
  }

  // Delete thumbnail image from Supabase Storage
  static async deleteThumbnail(thumbnailUrl: string): Promise<void> {
    try {
      // Extract the filename from the URL
      const url = new URL(thumbnailUrl)
      const pathParts = url.pathname.split('/')
      const fileName = pathParts[pathParts.length - 1]

      const { error } = await supabase.storage
        .from('video-thumbnails')
        .remove([fileName])

      if (error) {
        console.warn('Failed to delete thumbnail:', error)
        // Don't throw error for thumbnail deletion failures
      }
    } catch (error) {
      console.warn('Failed to parse thumbnail URL for deletion:', error)
    }
  }

  // Create demo video with uploaded thumbnail
  static async createDemoVideo(
    videoData: Omit<VideoInsert, 'vimeo_id' | 'video_type'> & {
      video_type: 'demo' | 'chat_only'
    },
    thumbnailFile?: File
  ): Promise<Video> {
    let thumbnailUrl = videoData.thumbnail_url

    // Upload thumbnail if provided
    if (thumbnailFile) {
      thumbnailUrl = await this.uploadThumbnail(thumbnailFile)
    }

    const demoVideoData: VideoInsert = {
      ...videoData,
      vimeo_id: null,
      thumbnail_url: thumbnailUrl,
      published_at: new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('videos')
      .insert(demoVideoData)
      .select()
      .single()

    if (error) {
      // Clean up uploaded thumbnail if video creation failed
      if (thumbnailUrl && thumbnailFile) {
        await this.deleteThumbnail(thumbnailUrl)
      }
      throw error
    }

    return data
  }

  // Update demo video with new thumbnail
  static async updateDemoVideo(
    id: string,
    updates: Omit<VideoUpdate, 'vimeo_id' | 'video_type'>,
    newThumbnailFile?: File
  ): Promise<Video> {
    const currentVideo = await this.getVideo(id)
    if (!currentVideo || (currentVideo.video_type !== 'demo' && currentVideo.video_type !== 'chat_only')) {
      throw new Error('Video is not a demo or chat_only video')
    }

    let updateData = { ...updates }

    // Handle thumbnail update
    if (newThumbnailFile) {
      // Upload new thumbnail
      const newThumbnailUrl = await this.uploadThumbnail(newThumbnailFile, id)
      updateData = { ...updateData, thumbnail_url: newThumbnailUrl }

      // Delete old thumbnail if it exists and is from our storage
      if (currentVideo.thumbnail_url?.includes('video-thumbnails')) {
        await this.deleteThumbnail(currentVideo.thumbnail_url)
      }
    }

    const { data, error } = await supabase
      .from('videos')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      // Clean up new thumbnail if update failed
      if (newThumbnailFile && updateData.thumbnail_url) {
        await this.deleteThumbnail(updateData.thumbnail_url)
      }
      throw error
    }

    return data
  }

  // Enhanced delete method that handles thumbnail cleanup
  static async deleteVideo(id: string): Promise<void> {
    // Get video info before deletion to clean up thumbnail
    const video = await this.getVideo(id)
    
    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', id)

    if (error) throw error

    // Clean up thumbnail if it's a demo or chat_only video with uploaded thumbnail
    if (video && (video.video_type === 'demo' || video.video_type === 'chat_only') && video.thumbnail_url?.includes('video-thumbnails')) {
      await this.deleteThumbnail(video.thumbnail_url)
    }
  }

  // ── School video methods ──────────────────────────────────────────────────

  static extractYouTubeId(input: string): string | null {
    if (!input) return null
    // Full URL patterns
    const urlMatch = input.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/)
    if (urlMatch) return urlMatch[1]
    // Bare ID (11 chars, alphanumeric + _ -)
    if (/^[A-Za-z0-9_-]{11}$/.test(input.trim())) return input.trim()
    return null
  }

  static async getSchoolVideos(schoolId: string): Promise<Video[]> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('school_id', schoolId)
      .eq('is_school_video', true)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data ?? []
  }

  // Get videos ranked by this school's viewing activity within the configured window.
  // Includes system-wide videos and the school's own videos; always returns up to `limit`
  // items (videos with no views in the window fall back to overall view_count order).
  // When the school has no views at all in the window, returns hasSchoolViews: false and no
  // videos, so the caller can show the all-schools ranking instead.
  static async getSchoolRankedVideos(
    schoolId: string,
    periodDays: number | null,
    limit = 15
  ): Promise<{ videos: VideoWithCategory[]; hasSchoolViews: boolean }> {
    const { data: rankings, error: rankingError } = await supabase.rpc('get_school_video_rankings', {
      p_school_id: schoolId,
      p_days: periodDays,
      p_limit: limit
    })
    if (rankingError) throw rankingError

    const rows: { video_id: string; school_view_count: number }[] = rankings || []
    const hasSchoolViews = rows.some(r => Number(r.school_view_count) > 0)
    if (!hasSchoolViews) return { videos: [], hasSchoolViews }
    const orderedIds = rows.map(r => r.video_id)

    const { data: videos, error: videosError } = await supabase
      .from('videos')
      .select(`
        *,
        categories!category_id (
          id,
          name,
          slug
        ),
        video_tags (
          tags (
            id,
            name,
            slug
          )
        )
      `)
      .in('id', orderedIds)
    if (videosError) throw videosError

    const byId = new Map<string, any>((videos || []).map(v => [v.id, v]))
    const ranked = orderedIds
      .map(id => byId.get(id))
      .filter(Boolean)
      .map(video => ({
        ...video,
        categories: video.categories || null,
        tags: video.video_tags?.map((vt: any) => ({ name: vt.tags.name, slug: vt.tags.slug })) || []
      }))
    return { videos: ranked, hasSchoolViews }
  }

  static async createSchoolVideo(data: {
    title: string
    description?: string | null
    youtube_id: string
    school_id: string
  }): Promise<Video> {
    const thumbnailUrl = `https://img.youtube.com/vi/${data.youtube_id}/hqdefault.jpg`
    const { data: video, error } = await supabase
      .from('videos')
      .insert({
        title: data.title,
        description: data.description ?? null,
        youtube_id: data.youtube_id,
        thumbnail_url: thumbnailUrl,
        school_id: data.school_id,
        is_school_video: true,
        category: '',
        tags: [],
        published_at: new Date().toISOString(),
      })
      .select()
      .single()
    if (error) throw error
    return video
  }

  static async createSchoolChatOnlyVideo(
    data: { title: string; description?: string | null; school_id: string; thumbnail_title?: string | null },
    thumbnailFile?: File
  ): Promise<Video> {
    const thumbnailUrl = thumbnailFile ? await this.uploadThumbnail(thumbnailFile) : null
    const { data: video, error } = await supabase
      .from('videos')
      .insert({
        title: data.title,
        description: data.description ?? null,
        thumbnail_url: thumbnailUrl,
        thumbnail_title: data.thumbnail_title ?? null,
        school_id: data.school_id,
        is_school_video: true,
        video_type: 'chat_only' as const,
        category: '',
        tags: [],
        published_at: new Date().toISOString(),
      })
      .select()
      .single()
    if (error) {
      if (thumbnailUrl) await this.deleteThumbnail(thumbnailUrl)
      throw error
    }
    return video
  }

  static async updateSchoolVideo(id: string, data: {
    title?: string
    description?: string | null
    youtube_id?: string
    thumbnail_url?: string
  }): Promise<Video> {
    const updates: VideoUpdate = { ...data }
    if (data.youtube_id) {
      updates.thumbnail_url = `https://img.youtube.com/vi/${data.youtube_id}/hqdefault.jpg`
    }
    const { data: video, error } = await supabase
      .from('videos')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return video
  }

  static async deleteSchoolVideo(id: string): Promise<void> {
    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', id)
    if (error) throw error
  }
}